// Pure criteria-list logic for the resource-groups builder, extracted out of
// AdminTagGroupsPage.tsx so it can be unit tested without rendering the page.

export interface Criterion {
  tag_id: string
  key: string
  value: string
}

export interface TagKeyMeta {
  key: string
  multi_value: boolean
}

export interface TagOption {
  id: string
  key: string
  value: string
}

// Two criteria on the same single-value key can never both be true for one
// resource — a real user mistake, not a stylistic nag. Multi-value keys
// (e.g. sensitivity) are excluded since AND-ing two values there is meaningful.
export function computeContradictoryKeys(criteria: Criterion[], tagKeys: TagKeyMeta[]): string[] {
  const counts = new Map<string, number>()
  for (const c of criteria) counts.set(c.key, (counts.get(c.key) ?? 0) + 1)
  const singleValueKeys = new Set(tagKeys.filter((k) => !k.multi_value).map((k) => k.key))
  return [...counts.entries()]
    .filter(([key, n]) => n > 1 && singleValueKeys.has(key))
    .map(([key]) => key)
}

// Order-independent comparison: the same criteria set added in a different
// order must not be reported as "changed since last preview".
export function sortedTagIdsKey(tagIds: string[]): string {
  return JSON.stringify([...tagIds].sort())
}

export function computeIsStale(lastPreviewedIds: string | null, criteriaTagIds: string[]): boolean {
  return lastPreviewedIds !== sortedTagIdsKey(criteriaTagIds)
}

export interface AddCriterionResult {
  criteria: Criterion[]
  error?: string
}

export function addCriterionToList(criteria: Criterion[], tag: TagOption, maxCriteria: number): AddCriterionResult {
  if (criteria.some((c) => c.tag_id === tag.id)) {
    return { criteria } // already present, no-op rather than a duplicate
  }
  if (criteria.length >= maxCriteria) {
    return { criteria, error: `Maximum ${maxCriteria} criteria per group` }
  }
  return { criteria: [...criteria, { tag_id: tag.id, key: tag.key, value: tag.value }] }
}

export function removeCriterionFromList(criteria: Criterion[], tagId: string): Criterion[] {
  return criteria.filter((c) => c.tag_id !== tagId)
}
